import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, take } from 'rxjs';
import { ApiService } from './api.service';
import { SubscriptionPause } from '../shared/types/subscription-pause.types';

/** The tag as the API reports it. A pause is not one; see cardStatus in subscription-pause.util. */
export type CustomerSubscriptionDisplayStatus = 'active' | 'cancelling' | 'cancelled' | 'expired';

/** One membership the customer holds or has held (CWS-1470). */
export interface CustomerSubscription {
  subscription_id: string;
  order_id: string | null;
  order_number: string | null;
  package_id: string;
  package_name: string;
  price: string | number | null;
  source: string;
  status: string;
  display_status: CustomerSubscriptionDisplayStatus;
  is_live: boolean;
  family_plan: boolean;
  /** Family plan tags (CWS-1582 §9): the membership belongs to a V2 family group, and is its primary. */
  family_group_id?: string | null;
  is_family?: boolean;
  is_primary?: boolean;
  vehicle_id: string | null;
  license_plate: string | null;
  vehicle_state: string | null;
  /** CWS-1536: the membership's car, "2022 Porsche 911"; absent from an older svc-order. */
  vehicle_year?: number | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  /** Sold in the Way B2C app rather than at the shop. */
  is_way_app?: boolean;
  /** The Way+ cover that came with the membership ('active', 'cancelled'), null for none. */
  way_plus_status?: string | null;
  started_at: string | null;
  ended_at: string | null;
  next_billing_date: string | null;
  /** End of the paid period; the pause pop-up projects the next charge from it. */
  current_period_end: string | null;
  last_payment_date: string | null;
  last_washed_date: string | null;
  unlimited: boolean;
  washes_available: number | null;
  washes_redeemed: number;
  washes_redeemed_total: number;
  can_change_plan: boolean;
  /**
   * CWS-1581: the server's answer for Start Family: a live Way membership on a family-enabled
   * package, not in a family, where Family Plan is on at its sale location.
   */
  can_start_family?: boolean;
  /**
   * CWS-1780: a live Way membership that is not already cancelling. The card offers Cancel
   * membership when this is true and cancel & refund is on at the location.
   */
  can_cancel: boolean;
  /** The vendor's member code on an imported row; null for a Way membership (CWS-1734). */
  member_code: string | null;
  /**
   * CWS-1734: the membership belongs to a sibling vendor customer record on the same
   * phone (CWS-1576), listed here so a split import still reads as one member.
   */
  is_linked_member: boolean;
  /** Null when membership pause is switched off (SUBSCRIPTION_PAUSE_ENABLED). */
  pause: SubscriptionPause | null;
}

/**
 * Memberships for the Customers → Subscriptions tab (CWS-1470).
 *
 * Read-only. Changing a plan goes through the existing plan-change endpoints
 * (CWS-1426) and pausing through SubscriptionPauseApiService, both keyed on `subscription_id`.
 */
@Injectable({ providedIn: 'root' })
export class CustomerSubscriptionService {
  constructor(private readonly apiService: ApiService) {}

  getCustomerSubscriptions(customerId: string): Observable<CustomerSubscription[]> {
    return this.apiService
      .get<CustomerSubscription[]>(`order/subscriptions/customer/${encodeURIComponent(customerId)}`)
      .pipe(map(response => response.data ?? []));
  }

  /** One membership of a customer, or null when it is not found or the read fails. */
  getSubscription(
    customerId: string,
    subscriptionId: string
  ): Observable<CustomerSubscription | null> {
    return this.getCustomerSubscriptions(customerId).pipe(
      take(1),
      map(list => list.find(sub => sub.subscription_id === subscriptionId) ?? null),
      catchError(() => of(null))
    );
  }
}
